import { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { sanitizeQuery } from '../lib/guardrails';
import Navbar from './navbar';
import Footer from './Footer';
import {
  FiSearch, FiStar, FiExternalLink, FiTrash2, FiAlertCircle,
  FiTrendingUp, FiTarget, FiInfo,
} from 'react-icons/fi';
import { STARTUP_API_URL, BASE_URL } from '../apiConfig';
import { motion } from 'framer-motion';
import RippleWaveLoader from './ui/RippleWaveLoader';

/* ─── interfaces ─────────────────────────────────────────────── */
interface AcquisitionTarget {
  id?: number;
  company_name: string;
  description?: string;
  website?: string;
  sector?: string;
  country?: string;
  stage?: string;
  founded_year?: string;
  employee_count?: string;
  estimated_valuation?: string;
  total_funding?: string;
  synergy_score?: number;
  confidence_score?: number;
  tech_alignment?: string;
  market_alignment?: string;
  integration_complexity?: string;
  recommendation?: string;
  created_on?: string;
  ai_signals?: { type: string; description: string; importance: string }[];
}

interface WatchlistItem {
  id: number;
  target_id: number;
  target: AcquisitionTarget;
  notes?: string;
  priority: string;
  status: string;
  created_on: string;
}

interface Signal {
  id: number;
  target_id: number;
  target_name?: string;
  signal_type: string;
  signal_description: string;
  signal_date?: string;
  importance?: string;
  source?: string;
}

/* ─── constants ──────────────────────────────────────────────── */
const SEARCH_LOADING_MESSAGES = [
  'Scanning acquisition databases…',
  'Scoring startups by strategic fit…',
  'Analyzing financial health indicators…',
  'Fetching valuation & funding data…',
  'Ranking targets by AI score…',
];

const QUICK_PROMPTS = [
  'AI startups in healthcare',
  'Manufacturing automation targets',
  'SaaS B2B under $10M valuation',
  'Climate tech startups USA',
  'Profitable fintech acquisition targets',
  'Deep tech startups early stage',
];

const SECTORS = ['AI/ML', 'Fintech', 'Healthcare', 'SaaS', 'Cybersecurity', 'E-commerce', 'EdTech', 'CleanTech'];
const REGIONS = ['United States', 'Europe', 'India', 'Southeast Asia', 'LATAM', 'Global'];
const PRIORITIES = ['high', 'medium', 'low'];
const STATUSES = ['evaluating', 'negotiating', 'due_diligence', 'closed', 'passed'];

/* ─── helpers ────────────────────────────────────────────────── */
const safeParse = <T,>(key: string, fallback: T): T => {
  try {
    const v = sessionStorage.getItem(key);
    return v ? (JSON.parse(v) as T) : fallback;
  } catch {
    return fallback;
  }
};

const getFaviconUrl = (website: string) => {
  try {
    return `https://www.google.com/s2/favicons?domain=${new URL(website).hostname}&sz=64`;
  } catch {
    return undefined;
  }
};

/* ═══════════════════════════════════════════════════════════════ */
const AcquisitionDashboardPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'search' | 'watchlist' | 'signals'>('search');

  /* ── search state ── */
  const [searchQuery, setSearchQuery] = useState('');
  const [sector, setSector] = useState('');
  const [geography, setGeography] = useState('India');
  const [results, setResults] = useState<AcquisitionTarget[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [lastQuery, setLastQuery] = useState('');

  /* ── autocomplete ── */
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [activeSuggestion, setActiveSuggestion] = useState(-1);
  const [suggestLoading, setSuggestLoading] = useState(false);
  const suggestWrapRef = useRef<HTMLDivElement>(null);

  /* ── watchlist / signals ── */
  const [watchlist, setWatchlist] = useState<WatchlistItem[]>([]);
  const [signals, setSignals] = useState<Signal[]>([]);
  const [savingIndex, setSavingIndex] = useState<number | null>(null);
  const [message, setMessage] = useState<{ text: string; type: 'success' | 'error' } | null>(null);

  const restoredRef = useRef(false);

  /* ── pre-fill and auto-search when navigated from chat ── */
  useEffect(() => {
    const q = location.state?.search_query;
    if (!q) return;
    const clean = sanitizeQuery(q);
    if (!clean) return;
    window.history.replaceState({}, document.title);
    runSearch(clean);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ── restore session on mount ── */
  useEffect(() => {
    if (restoredRef.current) return;
    restoredRef.current = true;
    const savedResults = safeParse<AcquisitionTarget[]>('acqDashResults', []);
    const savedQuery = sessionStorage.getItem('acqDashQuery') ?? '';
    const savedSearched = safeParse<boolean>('acqDashHasSearched', false);
    if (savedResults.length > 0) setResults(savedResults);
    if (savedQuery) setLastQuery(savedQuery);
    setHasSearched(savedSearched);
  }, []);

  /* ── persist search state ── */
  useEffect(() => {
    sessionStorage.setItem('acqDashResults', JSON.stringify(results));
    sessionStorage.setItem('acqDashQuery', lastQuery);
    sessionStorage.setItem('acqDashHasSearched', JSON.stringify(hasSearched));
  }, [results, lastQuery, hasSearched]);

  /* ── load watchlist / signals when tab switches ── */
  useEffect(() => {
    if (activeTab === 'watchlist') fetchWatchlist();
    else if (activeTab === 'signals') fetchSignals();
  }, [activeTab]);

  /* ── autocomplete debounce ── */
  useEffect(() => {
    const q = searchQuery.trim();
    if (loading || q.length < 3) {
      setSuggestions([]);
      setShowSuggestions(false);
      setActiveSuggestion(-1);
      return;
    }
    const controller = new AbortController();
    const timer = setTimeout(async () => {
      try {
        setSuggestLoading(true);
        const res = await fetch(`${BASE_URL}/api/autocomplete?q=${encodeURIComponent(q)}`, {
          signal: controller.signal,
        });
        const data = await res.json();
        const list: string[] = Array.isArray(data?.suggestions) ? data.suggestions.slice(0, 8) : [];
        setSuggestions(list);
        setShowSuggestions(list.length > 0);
        setActiveSuggestion(-1);
      } catch (e: any) {
        if (e?.name !== 'AbortError') console.error('[Autocomplete]', e);
      } finally {
        setSuggestLoading(false);
      }
    }, 250);
    return () => { clearTimeout(timer); controller.abort(); };
  }, [searchQuery, loading]);

  /* ─── search ─────────────────────────────────────────────── */
  const callSearchAPI = async (query: string, excludeCompanies: string[] = []) => {
    const safeQuery = sanitizeQuery(query);
    const res = await fetch(`${STARTUP_API_URL}/api/v1/acquisition/search`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': localStorage.getItem('authToken') || '',
      },
      body: JSON.stringify({
        search_query: safeQuery,
        sector: sector || undefined,
        geography: geography || undefined,
        exclude_companies: excludeCompanies,
      }),
    });
    return res.json();
  };

  const runSearch = async (overrideQuery?: string) => {
    const effectiveQuery = overrideQuery ?? searchQuery;
    if (!sanitizeQuery(effectiveQuery) && !sector && !geography) return;

    if (overrideQuery !== undefined) setSearchQuery(overrideQuery);

    setLoading(true);
    setHasSearched(true);
    setResults([]);
    setShowSuggestions(false);
    setSuggestions([]);
    setActiveSuggestion(-1);

    try {
      const data = await callSearchAPI(effectiveQuery, []);
      if (data.status === 'success') {
        setResults(data.data || []);
        setLastQuery(sanitizeQuery(effectiveQuery) || `${sector} ${geography}`.trim());
      }
    } catch (err) {
      console.error('Search error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleLoadMore = async () => {
    setLoadingMore(true);
    setMessage(null);
    try {
      const existing = results.map(r => r.company_name).filter(Boolean);
      const data = await callSearchAPI(searchQuery, existing);
      if (data.status === 'success' && data.data?.length) {
        const existingLower = new Set(existing.map(n => n.toLowerCase()));
        const unique = (data.data as AcquisitionTarget[]).filter(
          t => !existingLower.has((t.company_name || '').toLowerCase())
        );
        if (unique.length === 0) {
          setMessage({ text: 'No new results found. Try refining your search.', type: 'error' });
        } else {
          setResults(prev => [...prev, ...unique]);
        }
      } else if (data.status === 'fail') {
        const isRate = (data.message || '').toLowerCase().includes('rate limit');
        setMessage({
          text: isRate ? 'AI is busy — wait a moment and try again.' : data.message || 'Failed to load more.',
          type: 'error',
        });
      }
    } catch {
      setMessage({ text: 'Failed to load more results. Please try again.', type: 'error' });
    } finally {
      setLoadingMore(false);
      setTimeout(() => setMessage(null), 4000);
    }
  };

  const handleSuggestionSelect = (s: string) => {
    setSearchQuery(s);
    setShowSuggestions(false);
    setSuggestions([]);
    setActiveSuggestion(-1);
    setTimeout(() => runSearch(s), 0);
  };

  /* ─── watchlist ──────────────────────────────────────────── */
  const fetchWatchlist = async () => {
    setLoading(true);
    try {
      const userId = localStorage.getItem('uid') || 'default';
      const res = await fetch(`${STARTUP_API_URL}/api/v1/acquisition/watchlist?user_id=${userId}`, {
        headers: { 'Authorization': localStorage.getItem('authToken') || '' },
      });
      const data = await res.json();
      if (data.status === 'success') setWatchlist(data.data || []);
    } catch (err) {
      console.error('Watchlist fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const addToWatchlist = async (target: AcquisitionTarget, index: number) => {
    setSavingIndex(index);
    setMessage(null);
    try {
      const savePayload = {
        company_name: target.company_name,
        description: target.description,
        website: target.website,
        sector: target.sector,
        country: target.country,
        stage: target.stage,
        estimated_valuation: target.estimated_valuation,
        total_funding: target.total_funding,
        synergy_score: target.synergy_score,
        tech_alignment: target.tech_alignment,
        market_alignment: target.market_alignment,
        integration_complexity: target.integration_complexity,
        confidence_score: target.confidence_score,
        recommendation: target.recommendation,
        ai_signals: target.ai_signals || [],
      };
      const saveRes = await fetch(`${STARTUP_API_URL}/api/v1/acquisition/save`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': localStorage.getItem('authToken') || '',
        },
        body: JSON.stringify(savePayload),
      });
      const saveData = await saveRes.json();
      if (saveData.status === 'success') {
        const userId = localStorage.getItem('uid') || 'default';
        const wlRes = await fetch(`${STARTUP_API_URL}/api/v1/acquisition/watchlist?user_id=${userId}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': localStorage.getItem('authToken') || '',
          },
          body: JSON.stringify({ target_id: saveData.id, priority: 'medium', notes: '' }),
        });
        const wlData = await wlRes.json();
        if (wlData.status === 'success') {
          setWatchlist(prev => [...prev, {
            id: wlData.id, target_id: saveData.id, target,
            priority: 'medium', status: 'evaluating',
            created_on: new Date().toISOString(),
          } as WatchlistItem]);
          setMessage({ text: `${target.company_name} added to watchlist!`, type: 'success' });
        } else {
          setMessage({ text: wlData.message || 'Failed to add to watchlist', type: 'error' });
        }
      } else {
        setMessage({ text: saveData.message || 'Failed to save target', type: 'error' });
      }
    } catch {
      setMessage({ text: 'Could not connect to server.', type: 'error' });
    } finally {
      setSavingIndex(null);
      setTimeout(() => setMessage(null), 5000);
    }
  };

  const updateWatchlistItem = async (itemId: number, updates: { priority?: string; status?: string; notes?: string }) => {
    setWatchlist(prev => prev.map(item => item.id === itemId ? { ...item, ...updates } : item));
    try {
      const userId = localStorage.getItem('uid') || 'default';
      const res = await fetch(`${STARTUP_API_URL}/api/v1/acquisition/watchlist/${itemId}?user_id=${userId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': localStorage.getItem('authToken') || '' },
        body: JSON.stringify(updates),
      });
      const data = await res.json();
      if (data.status !== 'success') fetchWatchlist();
    } catch {
      fetchWatchlist();
    }
  };

  const removeFromWatchlist = async (itemId: number) => {
    if (!confirm('Remove from watchlist?')) return;
    try {
      const userId = localStorage.getItem('uid') || 'default';
      await fetch(`${STARTUP_API_URL}/api/v1/acquisition/watchlist/${itemId}?user_id=${userId}`, {
        method: 'DELETE',
        headers: { 'Authorization': localStorage.getItem('authToken') || '' },
      });
      fetchWatchlist();
    } catch (err) {
      console.error('Remove error:', err);
    }
  };

  /* ─── signals ────────────────────────────────────────────── */
  const fetchSignals = async () => {
    setLoading(true);
    try {
      const userId = localStorage.getItem('uid') || 'default';
      const res = await fetch(`${STARTUP_API_URL}/api/v1/acquisition/signals/watchlist?user_id=${userId}`, {
        headers: { 'Authorization': localStorage.getItem('authToken') || '' },
      });
      const data = await res.json();
      if (data.status === 'success') setSignals(data.data || []);
    } catch (err) {
      console.error('Signals fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  /* ─── style helpers ──────────────────────────────────────── */
  const getScoreColor = (score: number | undefined) => {
    if (!score) return 'text-gray-400';
    if (score >= 80) return 'text-green-400';
    if (score >= 60) return 'text-yellow-400';
    return 'text-red-400';
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high':   return 'bg-red-500/20 text-red-400';
      case 'medium': return 'bg-yellow-500/20 text-yellow-400';
      case 'low':    return 'bg-green-500/20 text-green-400';
      default:       return 'bg-gray-500/20 text-gray-400';
    }
  };

  /* ══════════════════════════════════════════════════════════ */
  return (
    <div
      className="min-h-screen flex flex-col text-white"
      style={{
        background:
          'radial-gradient(35% 19% at 50% 0%, rgba(255,84,31,0.45) 0%, rgba(0,0,0,0) 100%), black',
      }}
    >
      <Navbar />

      {/* dots pattern */}
      <div
        className="absolute pointer-events-none select-none"
        style={{
          width: 1106, height: 500, top: -12, left: 194,
          backgroundImage: "url('/dots.png')",
          backgroundSize: 'contain', backgroundRepeat: 'no-repeat',
          opacity: 1, zIndex: 0,
        }}
      />

      <section className="pt-32 pb-16 px-4 md:px-12 lg:px-24 relative z-10 flex-1">
        <div className="max-w-6xl mx-auto">

          {/* ── Header ── */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
            <button
              onClick={() => navigate("/")}
              className="flex items-center gap-1.5 text-sm text-gray-400 hover:text-white px-3 py-1.5 mb-4 rounded-lg hover:bg-white/10 transition-all border border-white/10"
            >
              ← Back to Chat
            </button>
            <h1 className="text-4xl md:text-5xl font-bold mb-2">
              M&amp;A <span className="text-[#FF541F]">Intelligence</span>
            </h1>
            <p className="text-gray-400 text-lg">
              Discover, score, and manage acquisition targets in one place
            </p>
          </motion.div>

          {/* ── Tabs ── */}
          <div className="flex gap-3 mb-8 border-b border-white/10 pb-4 overflow-x-auto">
            {([
              { key: 'search',    icon: <FiSearch size={17} />,    label: 'Discover Targets' },
              { key: 'watchlist', icon: <FiStar size={17} />,      label: `Watchlist (${watchlist.length})` },
              { key: 'signals',   icon: <FiAlertCircle size={17}/>, label: 'Deal Signals' },
            ] as const).map(tab => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-lg transition-all whitespace-nowrap ${
                  activeTab === tab.key
                    ? 'bg-[#FF541F] text-white'
                    : 'bg-[#1A1A1A] text-gray-400 hover:text-white'
                }`}
              >
                {tab.icon}{tab.label}
              </button>
            ))}
          </div>

          {/* ── Global message banner ── */}
          {message && (
            <div className={`mb-6 p-4 rounded-lg flex items-center justify-between border ${
              message.type === 'success'
                ? 'bg-green-500/20 text-green-400 border-green-500/30'
                : 'bg-red-500/20 text-red-400 border-red-500/30'
            }`}>
              <span>{message.text}</span>
              <button onClick={() => setMessage(null)} className="ml-4 hover:opacity-70 text-lg leading-none">&times;</button>
            </div>
          )}

          {/* ════════════════════════════════════════════════════
              SEARCH TAB
          ════════════════════════════════════════════════════ */}
          {activeTab === 'search' && (
            <div>
              {/* ── autocomplete search bar ── */}
              <div className="w-full max-w-3xl mx-auto mb-6" ref={suggestWrapRef}>
                <div className="relative">
                  <div className="flex items-center h-12 overflow-hidden">
                    <div className="flex-1 flex items-center h-full rounded-l-full border border-[#F9F9F9] bg-black/80 pr-2">
                      <input
                        type="text"
                        value={searchQuery}
                        onChange={e => setSearchQuery(e.target.value)}
                        onFocus={() => !loading && setShowSuggestions(suggestions.length > 0)}
                        onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
                        onKeyDown={e => {
                          if (e.key === 'ArrowDown') { e.preventDefault(); setShowSuggestions(true); setActiveSuggestion(i => Math.min(i + 1, suggestions.length - 1)); }
                          else if (e.key === 'ArrowUp') { e.preventDefault(); setActiveSuggestion(i => Math.max(i - 1, -1)); }
                          else if (e.key === 'Escape') { setShowSuggestions(false); setActiveSuggestion(-1); }
                          else if (e.key === 'Enter') {
                            if (activeSuggestion >= 0 && suggestions[activeSuggestion]) { handleSuggestionSelect(suggestions[activeSuggestion]); }
                            else runSearch();
                          }
                        }}
                        placeholder="Search acquisition targets (e.g., AI startups in manufacturing)"
                        className="flex-1 px-6 py-0 outline-none border-none bg-transparent text-[#F9F9F9] placeholder:text-white/30"
                        style={{ fontFamily: 'Montserrat, sans-serif', fontSize: 18 }}
                      />
                      {suggestLoading && (
                        <div className="mr-2"><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white/70" /></div>
                      )}
                    </div>
                    <button
                      onClick={() => runSearch()}
                      disabled={loading}
                      className="h-full bg-[#FF541F] hover:bg-orange-600 text-white font-semibold px-8 rounded-r-full transition flex items-center gap-2 disabled:opacity-60"
                      style={{ fontSize: 17 }}
                    >
                      {loading
                        ? <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                        : <><FiSearch /><span>Search</span></>}
                    </button>
                  </div>

                  {/* Autocomplete dropdown */}
                  {showSuggestions && !loading && suggestions.length > 0 && (
                    <div className="absolute left-0 right-0 mt-2 bg-[#0F0F10] border border-white/10 rounded-xl shadow-xl z-20 overflow-hidden">
                      {suggestions.map((s, idx) => (
                        <button
                          key={`${s}-${idx}`}
                          type="button"
                          onMouseDown={e => e.preventDefault()}
                          onClick={() => handleSuggestionSelect(s)}
                          className={`w-full text-left px-4 py-2 text-sm transition-colors ${
                            idx === activeSuggestion ? 'bg-white/10 text-white' : 'hover:bg-white/5 text-gray-200'
                          }`}
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Filters row */}
                <div className="grid grid-cols-2 gap-3 mt-3">
                  <select
                    value={sector}
                    onChange={e => setSector(e.target.value)}
                    className="px-4 py-2.5 rounded-lg bg-[#1A1A1A] border border-white/10 text-white text-sm focus:outline-none focus:border-[#FF541F]"
                  >
                    <option value="">All Sectors</option>
                    {SECTORS.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                  <select
                    value={geography}
                    onChange={e => setGeography(e.target.value)}
                    className="px-4 py-2.5 rounded-lg bg-[#1A1A1A] border border-white/10 text-white text-sm focus:outline-none focus:border-[#FF541F]"
                  >
                    <option value="">All Regions</option>
                    {REGIONS.map(r => <option key={r} value={r}>{r}</option>)}
                  </select>
                </div>

                {/* Quick prompts */}
                <div className="flex flex-wrap gap-2 mt-3 justify-center">
                  {QUICK_PROMPTS.map(prompt => (
                    <button
                      key={prompt}
                      onClick={() => { setSearchQuery(prompt); runSearch(prompt); }}
                      className="text-xs px-3 py-1.5 rounded-full border border-white/15 text-gray-400 hover:text-white hover:border-orange-500/50 transition-all"
                      style={{ background: 'rgba(255,255,255,0.04)' }}
                    >
                      {prompt}
                    </button>
                  ))}
                </div>

                {/* Last query pill */}
                {lastQuery && (
                  <div className="mt-3 flex justify-center">
                    <div className="inline-flex items-start gap-2 text-xs text-gray-300 bg-white/5 border border-white/10 rounded-md px-3 py-2">
                      <FiInfo className="mt-0.5 text-blue-300 shrink-0" />
                      <span className="text-gray-400">Last search:</span>&nbsp;
                      <span className="text-gray-200 break-all">{lastQuery}</span>
                    </div>
                  </div>
                )}
              </div>

              {/* ── loading ── */}
              {loading && (
                <div className="py-16">
                  <RippleWaveLoader messages={SEARCH_LOADING_MESSAGES} />
                </div>
              )}

              {/* ── empty state ── */}
              {!loading && !hasSearched && (
                <div className="text-center py-20 bg-[#1A1A1A]/60 rounded-xl border border-white/10">
                  <FiTarget size={48} className="mx-auto mb-4 text-[#FF541F]" />
                  <p className="text-gray-300 font-medium mb-1">Find your next acquisition</p>
                  <p className="text-gray-500 text-sm">Type a query above or pick a quick prompt to get started</p>
                </div>
              )}

              {!loading && hasSearched && results.length === 0 && (
                <div className="text-center py-12 bg-[#1A1A1A] rounded-xl border border-white/10">
                  <p className="text-gray-400">No targets found. Try different search criteria.</p>
                </div>
              )}

              {/* ── result cards ── */}
              {!loading && results.length > 0 && (
                <div className="space-y-4">
                  {results.map((target, index) => (
                    <motion.div
                      key={`${target.company_name}-${index}`}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.04 }}
                      className="bg-[#1A1A1A] rounded-xl p-6 border border-white/10 hover:border-[#FF541F]/50 transition-all"
                    >
                      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                        {/* ── left: logo + info ── */}
                        <div className="flex gap-4 flex-1 min-w-0">
                          {/* favicon / initial */}
                          {target.website ? (
                            <div className="w-12 h-12 rounded-xl overflow-hidden bg-white/5 border border-white/10 flex items-center justify-center flex-shrink-0">
                              <img
                                src={getFaviconUrl(target.website)}
                                alt=""
                                className="w-9 h-9 object-contain"
                                onError={e => {
                                  const el = e.target as HTMLImageElement;
                                  el.style.display = 'none';
                                  el.parentElement!.innerHTML = `<span class="text-lg font-bold text-white">${target.company_name.charAt(0)}</span>`;
                                }}
                              />
                            </div>
                          ) : (
                            <div className="w-12 h-12 rounded-xl bg-[#FF541F]/20 border border-[#FF541F]/20 flex items-center justify-center flex-shrink-0">
                              <span className="text-lg font-bold text-white">{target.company_name.charAt(0)}</span>
                            </div>
                          )}

                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                              <h3 className="text-lg font-semibold">{target.company_name}</h3>
                            </div>

                            {target.website && (
                              <a
                                href={target.website.startsWith('http') ? target.website : `https://${target.website}`}
                                target="_blank" rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 text-xs text-[#FF541F]/80 hover:text-[#FF541F] transition-colors mb-2"
                              >
                                <FiExternalLink size={11} />
                                {target.website.replace(/^https?:\/\/(www\.)?/, '')}
                              </a>
                            )}

                            <p className="text-gray-400 text-sm mb-3 line-clamp-2">{target.description}</p>

                            <div className="flex flex-wrap gap-2 mb-3">
                              {target.sector && <span className="px-2 py-0.5 text-xs bg-[#FF541F]/20 text-[#FF541F] rounded">{target.sector}</span>}
                              {target.stage && <span className="px-2 py-0.5 text-xs bg-blue-500/20 text-blue-400 rounded">{target.stage}</span>}
                              {target.country && <span className="px-2 py-0.5 text-xs bg-gray-500/20 text-gray-400 rounded">{target.country}</span>}
                              {target.founded_year && <span className="px-2 py-0.5 text-xs bg-purple-500/20 text-purple-400 rounded">Est. {target.founded_year}</span>}
                              {target.employee_count && <span className="px-2 py-0.5 text-xs bg-teal-500/20 text-teal-400 rounded">{target.employee_count} employees</span>}
                            </div>

                            <div className="flex flex-wrap gap-5 text-sm">
                              {target.synergy_score !== undefined && (
                                <div><span className="text-gray-500">Synergy: </span><span className={getScoreColor(target.synergy_score)}>{target.synergy_score}%</span></div>
                              )}
                              {target.confidence_score !== undefined && (
                                <div><span className="text-gray-500">Confidence: </span><span className={getScoreColor(target.confidence_score)}>{target.confidence_score}%</span></div>
                              )}
                              {target.estimated_valuation && (
                                <div><span className="text-gray-500">Valuation: </span><span className="text-green-400">{target.estimated_valuation}</span></div>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* ── right: action ── */}
                        <div className="flex-shrink-0">
                          <button
                            onClick={() => addToWatchlist(target, index)}
                            disabled={savingIndex === index}
                            className="flex items-center gap-2 px-4 py-2 bg-[#FF541F]/20 text-[#FF541F] rounded-lg hover:bg-[#FF541F]/30 transition-colors disabled:opacity-50 disabled:cursor-wait whitespace-nowrap"
                          >
                            {savingIndex === index ? (
                              <><div className="animate-spin rounded-full h-4 w-4 border-t-2 border-[#FF541F]" />Saving…</>
                            ) : (
                              <><FiStar size={15} />Save to Watchlist</>
                            )}
                          </button>
                        </div>
                      </div>

                      {target.recommendation && (
                        <div className="mt-4 p-3 bg-black/30 rounded-lg">
                          <p className="text-sm text-gray-300">
                            <span className="text-[#FF541F] font-medium">AI Recommendation: </span>
                            {target.recommendation}
                          </p>
                        </div>
                      )}
                    </motion.div>
                  ))}

                  {/* Load more */}
                  <div className="flex justify-center mt-6">
                    <button
                      onClick={handleLoadMore}
                      disabled={loadingMore}
                      className="px-8 py-3 bg-[#1A1A1A] border border-white/10 rounded-lg text-gray-300 hover:border-[#FF541F]/50 hover:text-white transition-all disabled:opacity-50 flex items-center gap-2"
                    >
                      {loadingMore ? (
                        <><div className="animate-spin rounded-full h-4 w-4 border-t-2 border-[#FF541F]" />Loading more…</>
                      ) : 'Load More Results'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ════════════════════════════════════════════════════
              WATCHLIST TAB
          ════════════════════════════════════════════════════ */}
          {activeTab === 'watchlist' && (
            <div>
              {/* Watchlist quick-filter chips */}
              {!loading && watchlist.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-5">
                  {[
                    { label: '🔴 High Priority', field: 'priority', val: 'high' },
                    { label: '🟡 Medium Priority', field: 'priority', val: 'medium' },
                    { label: '🟢 Low Priority',   field: 'priority', val: 'low' },
                    { label: 'Evaluating',         field: 'status',   val: 'evaluating' },
                    { label: 'Negotiating',        field: 'status',   val: 'negotiating' },
                    { label: 'Due Diligence',      field: 'status',   val: 'due_diligence' },
                  ].map(chip => (
                    <button
                      key={chip.label}
                      onClick={() =>
                        setWatchlist(prev =>
                          [...prev].sort((a, b) => {
                            const av = (chip.field === 'priority' ? a.priority : a.status) === chip.val ? -1 : 1;
                            const bv = (chip.field === 'priority' ? b.priority : b.status) === chip.val ? -1 : 1;
                            return av - bv;
                          })
                        )
                      }
                      className="text-xs px-3 py-1.5 rounded-full border border-white/15 text-gray-400 hover:text-white hover:border-orange-500/50 transition-all"
                      style={{ background: 'rgba(255,255,255,0.04)' }}
                    >
                      {chip.label}
                    </button>
                  ))}
                </div>
              )}

              {loading && (
                <div className="py-16">
                  <RippleWaveLoader messages={['Loading your watchlist…', 'Fetching saved targets…']} />
                </div>
              )}

              {!loading && watchlist.length === 0 && (
                <div className="text-center py-16 bg-[#1A1A1A] rounded-xl border border-white/10">
                  <FiStar size={48} className="mx-auto mb-4 text-gray-600" />
                  <p className="text-gray-400 mb-4">Your watchlist is empty</p>
                  <button
                    onClick={() => setActiveTab('search')}
                    className="px-6 py-2 bg-[#FF541F] rounded-lg hover:bg-[#FF541F]/80 transition-colors"
                  >
                    Discover Targets
                  </button>
                </div>
              )}

              {!loading && watchlist.length > 0 && (
                <div className="space-y-4">
                  {watchlist.map(item => (
                    <motion.div
                      key={item.id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="bg-[#1A1A1A] rounded-xl p-6 border border-white/10"
                    >
                      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2 flex-wrap">
                            <h3 className="text-lg font-semibold">{item.target?.company_name || 'Unknown'}</h3>
                            <span className={`px-2 py-0.5 text-xs rounded ${getPriorityColor(item.priority)}`}>{item.priority} priority</span>
                            <span className="px-2 py-0.5 text-xs bg-white/10 text-gray-400 rounded">{item.status.replace('_', ' ')}</span>
                          </div>
                          <p className="text-gray-400 text-sm mb-4">{item.target?.description}</p>
                          {item.notes && <p className="text-sm text-gray-500 italic mb-4">Notes: {item.notes}</p>}

                          <div className="flex gap-2 flex-wrap">
                            <select
                              value={item.priority}
                              onChange={e => updateWatchlistItem(item.id, { priority: e.target.value })}
                              className="px-3 py-1 text-sm bg-black/30 rounded border border-white/10 focus:outline-none"
                            >
                              {PRIORITIES.map(p => <option key={p} value={p}>{p}</option>)}
                            </select>
                            <select
                              value={item.status}
                              onChange={e => updateWatchlistItem(item.id, { status: e.target.value })}
                              className="px-3 py-1 text-sm bg-black/30 rounded border border-white/10 focus:outline-none"
                            >
                              {STATUSES.map(s => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
                            </select>
                          </div>
                        </div>

                        <div className="flex gap-2 items-start">
                          {item.target?.website && (
                            <a
                              href={item.target.website}
                              target="_blank" rel="noopener noreferrer"
                              className="p-2 text-gray-400 hover:bg-white/10 rounded-lg transition-colors"
                            >
                              <FiExternalLink size={18} />
                            </a>
                          )}
                          <button
                            onClick={() => removeFromWatchlist(item.id)}
                            className="p-2 text-red-400 hover:bg-red-500/20 rounded-lg transition-colors"
                          >
                            <FiTrash2 size={18} />
                          </button>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ════════════════════════════════════════════════════
              SIGNALS TAB
          ════════════════════════════════════════════════════ */}
          {activeTab === 'signals' && (
            <div>
              {/* Signals quick-filter chips */}
              {!loading && signals.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-5">
                  {[
                    { label: '🔴 High Importance', val: 'high' },
                    { label: '🟡 Medium Importance', val: 'medium' },
                    { label: '🟢 Low Importance', val: 'low' },
                  ].map(chip => (
                    <button
                      key={chip.label}
                      onClick={() =>
                        setSignals(prev =>
                          [...prev].sort((a, b) =>
                            a.importance === chip.val ? -1 : b.importance === chip.val ? 1 : 0
                          )
                        )
                      }
                      className="text-xs px-3 py-1.5 rounded-full border border-white/15 text-gray-400 hover:text-white hover:border-orange-500/50 transition-all"
                      style={{ background: 'rgba(255,255,255,0.04)' }}
                    >
                      {chip.label}
                    </button>
                  ))}
                </div>
              )}

              {loading && (
                <div className="py-16">
                  <RippleWaveLoader messages={[
                    'Scanning market signals…',
                    'Analyzing funding activity…',
                    'Fetching M&A intelligence…',
                    'Detecting acquisition indicators…',
                  ]} />
                </div>
              )}

              {!loading && signals.length === 0 && (
                <div className="text-center py-16 bg-[#1A1A1A] rounded-xl border border-white/10">
                  <FiTrendingUp size={48} className="mx-auto mb-4 text-gray-600" />
                  <p className="text-gray-400 mb-2">No deal signals yet</p>
                  <p className="text-gray-500 text-sm">Signals appear once you add targets to your watchlist</p>
                </div>
              )}

              {!loading && signals.length > 0 && (
                <div className="space-y-4">
                  {signals
                    .filter(s => s.signal_description && s.signal_description.toLowerCase() !== 'unknown')
                    .map(signal => (
                      <motion.div
                        key={signal.id}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="bg-[#1A1A1A] rounded-xl p-5 border border-white/10"
                      >
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="flex items-center gap-3 mb-2 flex-wrap">
                              <span className={`px-2 py-0.5 text-xs rounded font-medium ${
                                signal.importance === 'high'   ? 'bg-red-500/20 text-red-400' :
                                signal.importance === 'medium' ? 'bg-yellow-500/20 text-yellow-400' :
                                'bg-green-500/20 text-green-400'
                              }`}>
                                {signal.importance || 'medium'}
                              </span>
                              <span className="px-2 py-0.5 text-xs bg-[#FF541F]/20 text-[#FF541F] rounded">
                                {signal.signal_type}
                              </span>
                              <span className="text-sm text-gray-500">{signal.target_name}</span>
                            </div>
                            <p className="text-gray-300">{signal.signal_description}</p>
                            {signal.signal_date && (
                              <p className="text-xs text-gray-500 mt-2">{signal.signal_date}</p>
                            )}
                          </div>
                          {signal.source && (
                            <a href={signal.source} target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-white ml-4">
                              <FiExternalLink size={16} />
                            </a>
                          )}
                        </div>
                      </motion.div>
                    ))}
                </div>
              )}
            </div>
          )}

        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AcquisitionDashboardPage;
